import { ArrowLeftOutlined } from "@ant-design/icons";
import { Alert, Button, Select } from "antd";
import qs from "query-string";
import { useState } from "react";
import { UNKNOWN_ERROR_MESSAGE } from "../constants";
import { persistLinkState } from "../hooks/useLinkTracker";
import { captureException } from "../sentry";
import Store from "../stateStore";
import {
  extractLinkParamsFromState,
  getBackButtonCallback,
  getBackendUrl,
  showBackButton,
  STORES,
} from "../utils";

type ZohobooksProps = {
  clientSecret: string;
  onBack: () => void;
  onCancel: () => void;
  onComplete: (publicToken: string) => void;
};

// Zoho OAuth apps are data-center specific, so the authorize/token URLs differ
// per region. The value is the region code the backend expects (see
// getZohobooksTokenBaseUrlForRegion in rutter-backend).
export enum ZOHOBOOKS_REGION {
  GLOBAL = "us",
  UAE = "ae",
}

const ZOHOBOOKS_REGION_HUMAN_READABLE: Record<string, string> = {
  [ZOHOBOOKS_REGION.GLOBAL]: "Global",
  [ZOHOBOOKS_REGION.UAE]: "UAE",
};

// Only showing this change to specific customers for now
// https://linear.app/rutter/issue/FND-2339/zoho-books-auth-failed-due-to-ae-region
const ALLOWED_ORGS = [
  "0ab3dedf-1711-49b8-9ce3-7497aea9f1bc", // The Blessed org
  "cf84778b-b121-4ebd-a4c4-a86352b8f21d", // Alaan Test
  "22c2d117-dce6-4105-9e41-30c323a1faad", // Alaan
  "73feb08b-934a-4712-af52-e669a6797bd4", // Alaan KSA Test
  "8d52fd76-9dce-4636-927e-5a9596ca819c", // alaan.com
];

function Zohobooks({ onBack }: ZohobooksProps) {
  const store = Store.useStore();
  const organizationId = store.get("orgId");
  const autoPlatform = store.get("autoPlatform");
  const returnUrl = store.get("customReturnUrl");
  const [error, setError] = useState<string>("");
  const [region, setRegion] = useState<string>(ZOHOBOOKS_REGION.GLOBAL);

  const showDataCenterSelection = ALLOWED_ORGS.includes(organizationId);

  function handleAuthError(e: any) {
    captureException(e);
    if (e.response?.data?.error_message) {
      setError(e.response.data.error_message);
    } else {
      setError(UNKNOWN_ERROR_MESSAGE);
    }
  }

  function handleComplete() {
    try {
      persistLinkState(STORES.ZOHOBOOKS, store.getState());
      const q = qs.stringify({
        ...extractLinkParamsFromState(store),
        region,
      });
      const url = `${getBackendUrl()}/zohobooks/proxy/${organizationId}/app-url?${q}`;
      window.location.replace(url);
    } catch (e) {
      handleAuthError(e);
    }
  }

  // Preserve the previous seamless behavior for integrations that pass
  // autoLoad — the region comes from the query param (defaulting to us).
  if (!showDataCenterSelection) {
    handleComplete();
    return null;
  }

  return (
    <>
      <div style={{ padding: "1.6rem" }}>
        {showDataCenterSelection && (
          <>
            <div style={{ display: "flex" }}>
              {showBackButton(autoPlatform, returnUrl) && (
                <Button
                  onClick={() =>
                    getBackButtonCallback(autoPlatform, onBack, returnUrl)
                  }
                  icon={<ArrowLeftOutlined />}
                  className="flex items-center"
                >
                  Back
                </Button>
              )}
            </div>
            <div className="font-semibold mt-4">Select your region</div>
            <div className="text-sm">
              Choose the Zoho data center where your account is hosted. Most
              accounts use Global; select UAE if you sign in at
              accounts.zoho.ae.
            </div>
            <Select
              style={{ width: "100%", marginTop: 20, marginBottom: 20 }}
              placeholder="Select region"
              onSelect={(val: any) => setRegion(val as string)}
              defaultValue={region}
            >
              {Object.values(ZOHOBOOKS_REGION).map((r) => (
                <Select.Option key={r} value={r}>
                  {ZOHOBOOKS_REGION_HUMAN_READABLE[r]}
                </Select.Option>
              ))}
            </Select>
            <Button
              type="primary"
              block
              disabled={!region}
              onClick={handleComplete}
            >
              Connect Zoho Books
            </Button>
          </>
        )}

        {error && (
          <Alert
            className="mt-4"
            type="error"
            message={"Error"}
            description={error}
            showIcon
          />
        )}
      </div>
    </>
  );
}

export default Zohobooks;
